import React from "react";
import LoadData from "../../../Components/LoadData";
import { LoadApiGoogleMapsContext } from "./LoadApiGoogleMaps";

const timeout = {
  onChange: setTimeout(() => {}, 0),
};
const TIME = 2000;

export interface AutocompleteProps {
  style?: React.CSSProperties;
  className?: string;
  id: string;
  type: "Place" | "Query";
  onChange?: (p: google.maps.places.QueryAutocompletePrediction) => void;
  options?: {
    bounds?: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral;
    componentRestrictions?: google.maps.places.ComponentRestrictions;
    location?: google.maps.LatLng;
    offset?: number;
    origin?: google.maps.LatLng | google.maps.LatLngLiteral;
    radius?: number;
    sessionToken?: google.maps.places.AutocompleteSessionToken;
    types?: string[];
  };
  inputValue: string;
  itemIcon?: JSX.Element;
  maxHeight?: string;
}

export const Autocomplete: React.FC<AutocompleteProps> = (props) => {
  const { isLoaded } = React.useContext(LoadApiGoogleMapsContext);
  const isMountedComponent = React.useRef<boolean>(false);
  const {
    children,
    style,
    className,
    id,
    type,
    onChange,
    options,
    inputValue,
    itemIcon,
    maxHeight,
  } = props;
  const [mounted, setMounted] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<any>();

  const [predictions, setPredictions] = React.useState<
    google.maps.places.QueryAutocompletePrediction[]
  >([]);
  const inputRef = React.useRef<HTMLInputElement | null | undefined>();
  const [isFocus, setIsFocus] = React.useState<boolean>(false);

  const _inputValue = React.useMemo(() => {
    return inputValue.trim() || "";
  }, [inputValue]);

  const autocompleteService = React.useMemo(() => {
    return mounted && isLoaded
      ? new google.maps.places.AutocompleteService()
      : undefined;
  }, [mounted, isLoaded]);

  const _options = React.useMemo(() => {
    const input = _inputValue;
    const c: google.maps.places.AutocompletionRequest = {
      input,
      ...options,
    };
    return input ? c : undefined;
  }, [_inputValue, options]);

  const displaySuggestions = (
    result: google.maps.places.QueryAutocompletePrediction[] | null,
    status: google.maps.places.PlacesServiceStatus
  ) => {
    if (isMountedComponent.current) {
      setLoading(() => false);
      if (status === google.maps.places.PlacesServiceStatus.OK && result) {
        setPredictions(() => result);
      } else {
        setPredictions(() => []);
      }
    }
  };

  const handleSelectPlace = (
    p: google.maps.places.QueryAutocompletePrediction
  ) => {
    if (inputRef.current) {
      inputRef.current.blur();
      inputRef.current.value = p.description;
    }
    if (onChange) {
      onChange(p);
    }
  };

  const handleOnFocus = () => setIsFocus(() => true);

  const handleOnBlur = () => {
    let t = setTimeout(() => {}, 0);
    clearTimeout(t);
    t = setTimeout(() => {
      if (isMountedComponent.current) {
        setIsFocus(() => false);
      }
    }, 200);
  };

  const handleOnRef = (r: HTMLInputElement | null | undefined) => {
    if (isMountedComponent.current) {
      inputRef.current = r;
      if (inputRef.current) {
        inputRef.current.removeEventListener("focus", handleOnFocus);
        inputRef.current.removeEventListener("blur", handleOnBlur);
        inputRef.current.addEventListener("focus", handleOnFocus);
        inputRef.current.addEventListener("blur", handleOnBlur);
      }
    }
  };

  React.useEffect(() => {
    if (!_inputValue) {
      clearTimeout(timeout.onChange);
      setLoading(() => false);
      setPredictions(() => []);
    }
  }, [_inputValue]);

  React.useEffect(() => {
    if (_options && autocompleteService) {
      clearTimeout(timeout.onChange);
      timeout.onChange = setTimeout(() => {
        if (type === "Query") {
          autocompleteService.getQueryPredictions(_options, displaySuggestions);
        } else {
          autocompleteService.getPlacePredictions(_options, displaySuggestions);
        }
      }, TIME);
    }
  }, [autocompleteService, type, _options]);

  React.useEffect(() => {
    isMountedComponent.current = true;
    setMounted(() => true);
    return () => {
      isMountedComponent.current = false;
      if (inputRef.current) {
        inputRef.current.removeEventListener("focus", handleOnFocus);
        inputRef.current.removeEventListener("blur", handleOnBlur);
      }
    };
  }, []);

  return (
    <div
      style={{
        width: "100%",
        ...style,
      }}
      className={className}
    >
      <InputRef
        id={id}
        onChange={() => setLoading(() => true)}
        onRef={handleOnRef}
      >
        {children}
      </InputRef>
      {_inputValue && isFocus ? (
        <div
          style={{
            width: "90%",
            position: "absolute",
            left: 0,
            zIndex: 1,
            margin: "1px 5%",
            padding: "4px",
            background: "white",
            boxShadow:
              "0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12)",
            borderRadius: "2px",
          }}
        >
          <div
            style={{
              maxHeight: maxHeight || "200px",
              overflowY: "scroll",
              padding: "0 4px",
            }}
          >
            <LoadData
              description="No hay resultados"
              empty={!predictions.length}
              loading={loading}
            >
              {predictions.map((p, i) => (
                <div
                  style={{ cursor: "pointer" }}
                  key={i}
                  onClick={() => handleSelectPlace(p)}
                >
                  {i ? (
                    <div
                      style={{
                        width: "100%",
                        height: "1px",
                        background: "#efefef",
                      }}
                    />
                  ) : null}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        marginRight: "4px",
                      }}
                    >
                      {itemIcon}
                    </div>
                    <p style={{ fontSize: "14px", margin: "8px 0" }}>
                      {p.description}
                    </p>
                  </div>
                </div>
              ))}
            </LoadData>
          </div>
          <div
            style={{
              padding: "0 4px",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "1px",
                background: "#efefef",
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "4px",
              height: "22px",
            }}
          >
            <img src={poweredByGoogleOnWhite} alt="poweredByGoogleOnWhite" />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export interface InputRefProps {
  style?: React.CSSProperties;
  className?: string;
  id: string;
  onRef?: (ref: HTMLInputElement | null | undefined) => void;
  onChange?: (value: string) => void;
}

export const InputRef: React.FC<InputRefProps> = (props) => {
  const { children, id, onRef, onChange, style, className } = props;
  const inputRef = React.useRef<HTMLInputElement | null | undefined>();

  const handleOnChange = (ev: any) => {
    const event = ev as React.ChangeEvent<HTMLInputElement>;
    if (onChange) {
      onChange(event.target.value);
    }
  };

  const handleOnRef = () => {
    if (inputRef.current) {
      inputRef.current.removeEventListener("input", handleOnChange);
    }
    const input = document.getElementById(id) as HTMLInputElement;
    if (input) {
      if (onRef) {
        onRef(input);
      }
      inputRef.current = input;
      inputRef.current.addEventListener("input", handleOnChange);
    }
  };

  React.useEffect(() => {
    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener("input", handleOnChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={style} className={className} ref={handleOnRef}>
      {children}
    </div>
  );
};

const poweredByGoogleOnWhite =
  "data:image/png;base64,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";
